/**
 * Role-Based Route Component
 *
 * Restricts access using the Access Decision (`useAccess` / Session JSON `can`).
 * Empty roles are not logout: invited-waiting still lands on `/signup-required`
 * immediately (no 8×400ms poll).
 */

import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Action, Subject } from '@/components/authnz/types/rbac';
import { useAuth } from '@/contexts/AuthContext';
import { useAccess } from '@/lib/access/client';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import logger from '@/lib/logging';
import { logRoutingDecision } from '@/lib/routing-debug';

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredAction: Action;
  requiredSubject: Subject;
  fallbackPath?: string;
}

/**
 * Component that restricts access based on the Access Decision
 * - If user has no roles assigned: redirects to fallback path immediately (default: /signup-required)
 * - If user has roles but lacks specific permission: displays error message and error state on current page
 */
export function RoleBasedRoute({
  children,
  requiredAction,
  requiredSubject,
  fallbackPath = '/signup-required',
}: RoleBasedRouteProps) {
  const { decision, loading: accessLoading } = useAccess();
  const { user, loading: authLoading, serverUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const toastShownRef = useRef(false);

  useEffect(() => {
    toastShownRef.current = false;
  }, [requiredAction, requiredSubject, location.pathname]);

  const userRoles = serverUser?.roles || [];
  const sessionPending = !!user && !serverUser;

  if (!authLoading && !accessLoading && sessionPending) {
    return <Navigate to="/login" replace />;
  }

  if (authLoading || accessLoading) {
    logRoutingDecision('loading', {
      requestContext: {
        path: location.pathname,
      },
      authState: {
        authenticated: !!user,
        userId: user?.uid || undefined,
        userEmail: user?.email || undefined,
        roles: serverUser?.roles || [],
      },
      additionalInfo: {
        requiredAction,
        requiredSubject,
        authLoading,
        accessLoading,
      },
    });
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  const hasPermission = decision.can(requiredAction, requiredSubject);
  const packedRules = decision.toJSON().rules.map((rule) => `${rule.action} ${rule.subject}`);

  logger.debug('[RoleBasedRoute] Permission check:', {
    action: requiredAction,
    subject: requiredSubject,
    hasPermission,
    user: user?.email,
    uid: user?.uid,
    authLoading,
    accessLoading,
    roles: serverUser?.roles || [],
    reason: decision.reason.kind,
  });

  if (!hasPermission) {
    const hasNoRoles = userRoles.length === 0;
    const denyReason = hasNoRoles ? 'missing_role' : 'insufficient_permissions';

    if (hasNoRoles) {
      logger.error(
        `❌ [RoleBasedRoute] Access denied: User has no roles assigned - Redirecting to ${fallbackPath}`,
        {
          user: user?.email,
          uid: user?.uid,
          action: requiredAction,
          subject: requiredSubject,
          fallbackPath,
          roles: [],
        },
      );

      logRoutingDecision('redirect', {
        redirectDestination: fallbackPath,
        redirectReason: 'missing_role',
        authState: {
          authenticated: !!user,
          userId: user?.uid || undefined,
          userEmail: user?.email || undefined,
          roles: [],
          abilities: packedRules,
        },
        additionalInfo: {
          requiredAction,
          requiredSubject,
          hasPermission,
          reason: 'User has no roles assigned',
        },
      });

      return <Navigate to={fallbackPath} replace />;
    }

    if (!toastShownRef.current) {
      toast({
        title: 'Access Denied',
        description: `You don't have permission to access this page. Required: ${requiredAction} ${String(requiredSubject)}`,
        variant: 'destructive',
      });
      toastShownRef.current = true;
    }

    logger.error(
      `❌ [RoleBasedRoute] Access denied: User lacks permission to ${requiredAction} ${String(requiredSubject)}`,
      {
        user: user?.email,
        uid: user?.uid,
        action: requiredAction,
        subject: requiredSubject,
        fallbackPath,
        roles: userRoles,
      },
    );

    logRoutingDecision('deny', {
      redirectReason: denyReason,
      authState: {
        authenticated: !!user,
        userId: user?.uid || undefined,
        userEmail: user?.email || undefined,
        roles: userRoles,
        abilities: packedRules,
      },
      additionalInfo: {
        requiredAction,
        requiredSubject,
        hasPermission,
        reason: `User lacks permission to ${requiredAction} ${String(requiredSubject)}`,
      },
    });

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-2xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-4">
                You don't have permission to access this page.
              </p>
              <div className="space-y-2 text-sm">
                <div>
                  <strong>Required permission:</strong> {requiredAction} {String(requiredSubject)}
                </div>
                {userRoles.length > 0 && (
                  <div>
                    <strong>Your roles:</strong> {userRoles.join(', ')}
                  </div>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
                <Button variant="outline" onClick={() => navigate(-1)}>
                  Go Back
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  logger.debug(
    `✅ [RoleBasedRoute] Access granted: User can ${requiredAction} ${String(requiredSubject)}`,
    {
      user: user?.email,
      uid: user?.uid,
      roles: serverUser?.roles || [],
    },
  );

  logRoutingDecision('allow', {
    authState: {
      authenticated: true,
      userId: user?.uid || undefined,
      userEmail: user?.email || undefined,
      roles: serverUser?.roles || [],
      abilities: packedRules,
    },
    additionalInfo: {
      requiredAction,
      requiredSubject,
      hasPermission: true,
    },
  });

  return <>{children}</>;
}
